import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import {
  CalendarDays, Sun, Flame, BookOpen, ScrollText, Sparkles,
  PartyPopper, CircleDot, Users, Landmark, Flower2, ArrowRight,
} from "lucide-react";

// Single source of truth for section cross-links → an "active flow" between pages.
const S = {
  panchang: { to: "/panchang", Icon: Sun, deva: "पंचांग", en: "Panchangam", sub: { deva: "आज की तिथि एवं मुहूर्त", en: "Today's tithi & muhurta" } },
  calendar: { to: "/calendar", Icon: CalendarDays, deva: "पर्व-पंचांग", en: "Festival Calendar", sub: { deva: "आगामी पर्व एवं व्रत", en: "Upcoming festivals & vratas" } },
  rituals: { to: "/rituals", Icon: Flame, deva: "पूजा विधि", en: "Pooja Vidhi", sub: { deva: "चरण-दर-चरण विधि", en: "Step-by-step guides" } },
  mantras: { to: "/mantras", Icon: BookOpen, deva: "मंत्र", en: "Mantras", sub: { deva: "बीज एवं वैदिक मंत्र", en: "Bija & Vedic mantras" } },
  stotras: { to: "/stotras", Icon: ScrollText, deva: "स्तोत्र", en: "Stotras", sub: { deva: "स्तुति एवं स्तोत्र", en: "Hymns of praise" } },
  deities: { to: "/deities", Icon: Sparkles, deva: "देवी-देवता", en: "Deities", sub: { deva: "देवताओं का परिचय", en: "Meet the deities" } },
  festivals: { to: "/festivals", Icon: PartyPopper, deva: "पर्व एवं उत्सव", en: "Festivals", sub: { deva: "पर्वों का महत्व एवं विधि", en: "Significance & observance" } },
  japa: { to: "/japa", Icon: CircleDot, deva: "जप", en: "Japa", sub: { deva: "माला एवं नाम-जप गणक", en: "Mala & naam-japa counter" } },
  groups: { to: "/groups", Icon: Users, deva: "नामस्मरण समूह", en: "Namasmarana Groups", sub: { deva: "साथ मिलकर नाम-जप करें", en: "Chant together as a sangha" } },
  dainik: { to: "/dainik-pooja", Icon: Flower2, deva: "दैनिक पूजा", en: "Daily Pooja", sub: { deva: "नित्य पूजन विधि", en: "Everyday nitya-puja" } },
  temples: { to: "/temples", Icon: Landmark, deva: "मंदिर", en: "Temples of Bharat", sub: { deva: "तीर्थ एवं मंदिर", en: "Sacred pilgrimage sites" } },
  pilgrimage: { to: "/pilgrimage", Icon: Flower2, deva: "तीर्थ यात्रा", en: "Pilgrimage", sub: { deva: "चार धाम एवं तीर्थ-क्षेत्र", en: "Char Dham & sacred circuits" } },
};

export default function JourneyStrip({ links = [], title, className = "" }) {
  const { pick } = useLang();
  const items = links.map((k) => S[k]).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <section data-testid="journey-strip" className={`mt-16 ${className}`}>
      <div className="mb-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-line" />
        <p className="overline-label text-gold">{title || pick("आगे बढ़ें", "Continue your journey")}</p>
        <span className="h-px flex-1 bg-line" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <Link key={it.to} to={it.to} data-testid={`journey-${it.to.replace(/\//g, "")}`}
            className="group flex items-center gap-3 border border-line bg-surface p-4 transition-colors hover:border-saffron/50 hover:bg-[rgba(199,80,0,0.03)]">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgba(199,80,0,0.08)] text-saffron">
              <it.Icon className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-serif-latin text-base text-ink transition-colors group-hover:text-saffron">{pick(it.deva, it.en)}</span>
              <span className="block truncate text-xs text-ink-3">{pick(it.sub.deva, it.sub.en)}</span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-ink-3 opacity-0 transition-opacity group-hover:opacity-70" />
          </Link>
        ))}
      </div>
    </section>
  );
}
